import React from 'react'
import { maybePluralizeWithNum } from '@chegg-wt/core/utils/language'

import { MainWrapper } from '@chegg-wt/core/components/Layouts/styled'
import { getMappedMonth } from '../../utils/dateTime'
import ResultCard from '../SourceSearchResults/ResultCard'
import SearchResultsEmpty from './SearchResultsEmpty'
import InvalidSearch from './InvalidSearchResult'
import SearchResultsLoading from './SearchResultsLoading'
import Error from './SearchResultsError'
import { SearchResultsContentProps } from './types'
import * as Styled from './styled'

export const EMPTY_HEADING = "We couldn't find this source in our library..."
export const EMPTY_COPY =
  'Try your search again with different keywords or create your citation manually.'

const ResultsText: React.FC<{ count: number }> = ({ count }) => (
  <Styled.ResultsTextContainer role="alert">
    <Styled.ResultText>
      {maybePluralizeWithNum(count, 'result', 'results')}
    </Styled.ResultText>{' '}
    found
  </Styled.ResultsTextContainer>
)

export const SearchResultsContent: React.FunctionComponent<SearchResultsContentProps> = ({
  afterFirstSource,
  afterSecondSource,
  sources,
  loading,
  error,
  sourceType,
  citationStyle,
  onSelectSource,
  invalidSearch,
  journalType,
  onChangeSourceType,
  onManualCiteClick,
  onChangeJournalType,
}) => {
  const advertAfterFirst = !!afterFirstSource && (
    <React.Fragment key="afterFirst">{afterFirstSource}</React.Fragment>
  )
  const advertAfterSecond = !!afterSecondSource && (
    <React.Fragment key="afterSecond">{afterSecondSource}</React.Fragment>
  )

  const handleCite = (result, index?: number) => {
    const source = result.data.source as string
    if (source === 'journal' && result.data?.pubjournal?.month) {
      const mappedMonth = getMappedMonth(result.data.pubjournal.month)
      const mappedResult = {
        ...result,
        data: { ...result.data, pubjournal: { ...result.data.pubjournal } },
      }
      mappedResult.data.pubjournal.month = mappedMonth
      return onSelectSource(mappedResult, index)
    }
    onSelectSource(result, index)
  }

  const items =
    sources?.length && !invalidSearch
      ? sources.map((result, i) => (
          <ResultCard
            key={i} // eslint-disable-line
            result={result}
            currentStyle={citationStyle}
            cite={() => handleCite(result, i)}
            index={i}
          />
        ))
      : []

  const [firstSource, secondSource, ...restSources] = items
  const firstSourceWithMainWrapper = firstSource ? (
    <MainWrapper key="mainWrapper">{firstSource}</MainWrapper>
  ) : null

  const sourceItems = [
    firstSourceWithMainWrapper,
    advertAfterFirst,
    secondSource,
    advertAfterSecond,
    ...restSources,
  ]

  const getContent = () => {
    if (invalidSearch) {
      return (
        <>
          <ResultsText count={0} />
          <InvalidSearch
            sourceType={sourceType}
            onChangeSourceType={onChangeSourceType}
            onManualCiteClick={onManualCiteClick}
          />
        </>
      )
    }

    if (loading)
      return (
        <MainWrapper>
          <SearchResultsLoading />
        </MainWrapper>
      )

    if (error)
      return (
        <MainWrapper>
          <Error
            sourceType={sourceType}
            journalType={journalType}
            onManualCiteClick={onManualCiteClick}
            onChangeJournalType={onChangeJournalType}
          />
        </MainWrapper>
      )

    if (!sources) return null

    if (sources.length === 0) {
      return (
        <>
          <ResultsText count={sources.length} />
          <MainWrapper>
            <SearchResultsEmpty
              heading={EMPTY_HEADING}
              copy={EMPTY_COPY}
              image={<Styled.EmptyImage />}
              sourceType={sourceType}
            />
          </MainWrapper>
        </>
      )
    }
    return (
      <>
        <ResultsText count={sources.length} />
      </>
    )
  }

  return (
    <>
      {getContent()}
      {sourceItems}
    </>
  )
}

export default SearchResultsContent
